import { FlaskConical } from 'lucide-react'
import type { SplitPreview } from '@/types/products'
import { formatLimsValue } from '@/lib/limsResults'
import {
  cellVolumeNote,
  childVolumeNote,
  limsLabelFor,
  missingMeasuredMessage,
  orderPreviewRows,
  missingWeightMessage,
  unavailableMessage,
} from '@/lib/splitPreview'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

/**
 * The split's arithmetic, shown before it runs.
 *
 * z8nrz7dkvf asks for the recalculation to be VISIBLE rather than taken on
 * trust: for every aliquot, what the source product held, what the aliquot
 * would be given, and the formula that turns one into the other. Collapsed by
 * default — an operator splitting a bag does not need fifteen rows per aliquot
 * in their way — and it is the place a reviewer goes when a dose looks wrong.
 *
 * Every figure and every formula string comes from the server
 * (`POST /products/:id/split/preview`), produced by the same pass that will
 * store the results. Nothing here computes a dose, and nothing here should
 * start to: a preview worked out in the browser would be a second
 * implementation of the standard, free to disagree with what gets saved.
 */
export function SplitLimsPreview({
  preview,
  isLoading,
  error,
}: {
  preview: SplitPreview | undefined
  isLoading: boolean
  error: Error | null
}) {
  const notes = preview
    ? [
        preview.unavailable ? unavailableMessage(preview.unavailable) : null,
        missingMeasuredMessage(preview),
        missingWeightMessage(preview),
      ].filter((n): n is string => !!n)
    : []

  return (
    <Accordion type='single' collapsible className='rounded-lg border'>
      <AccordionItem value='lims' className='border-b-0'>
        <AccordionTrigger className='px-3 py-2.5 text-sm hover:no-underline'>
          <span className='flex items-center gap-2'>
            <FlaskConical className='h-4 w-4 text-muted-foreground' />
            How the LIMS results are recalculated
            {notes.length > 0 && (
              <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-900'>
                {notes.length === 1 ? '1 note' : `${notes.length} notes`}
              </span>
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent className='space-y-3 px-3'>
          {isLoading && <p className='text-sm text-muted-foreground'>Calculating…</p>}

          {error && (
            <p className='text-sm text-destructive'>
              The recalculation preview could not be loaded: {error.message}
            </p>
          )}

          {preview && (
            <>
              <dl className='grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2'>
                {cellVolumeNote(preview) && (
                  <div className='sm:col-span-2'>{cellVolumeNote(preview)}</div>
                )}
                <div>
                  Recipient weight:{' '}
                  {preview.patientWeightKg != null ? (
                    <span className='font-mono'>{preview.patientWeightKg} kg</span>
                  ) : (
                    'not recorded'
                  )}
                </div>
                {/*
                  Same reason the LIMS details dialog says it: until the feed
                  exists the readings are synthetic, and a reviewer has to be
                  able to tell a made-up dose from a measured one.
                */}
                {preview.source.resultsSource === 'mock' && (
                  <div>Readings come from a mock source, not a real instrument.</div>
                )}
              </dl>

              {notes.map(note => (
                <p
                  key={note}
                  className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'
                >
                  {note}
                </p>
              ))}

              {preview.children.map((child, i) => (
                <div key={child.productDescriptionCode ?? i} className='space-y-1'>
                  <h4 className='text-xs font-medium'>
                    <span className='font-mono'>{child.productDescriptionCode ?? `Aliquot ${i + 1}`}</span>
                    <span className='ml-2 font-normal text-muted-foreground'>
                      {childVolumeNote(child)}
                    </span>
                  </h4>
                  {child.rows.length === 0 ? (
                    <p className='text-xs text-muted-foreground'>
                      Nothing to recalculate for this aliquot.
                    </p>
                  ) : (
                    <div className='overflow-hidden rounded-lg border'>
                      <table className='w-full text-xs'>
                        <thead className='bg-muted/30 text-left uppercase text-muted-foreground'>
                          <tr>
                            <th className='px-3 py-1.5 font-medium'>Result</th>
                            <th className='px-3 py-1.5 font-medium'>Source</th>
                            <th className='px-3 py-1.5 font-medium'>Aliquot</th>
                            <th className='px-3 py-1.5 font-medium'>How it was calculated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderPreviewRows(child.rows).map(row => (
                            <tr key={row.key} className='border-t align-top'>
                              <td className='px-3 py-1.5'>{limsLabelFor(row)}</td>
                              {/*
                                A dash, not a zero, and not the aliquot's own
                                figure: the source carries the five measured
                                results, and the totals derived from them are
                                calculated per bag rather than stored on it.
                              */}
                              <td className='px-3 py-1.5 font-mono tabular-nums text-muted-foreground'>
                                {row.before == null ? '—' : formatLimsValue(row.before)}
                              </td>
                              <td className='px-3 py-1.5 font-mono tabular-nums'>
                                {formatLimsValue(row.after)}
                              </td>
                              <td className='px-3 py-1.5'>
                                <span className='font-mono'>{row.substituted}</span>
                                <span className='block text-muted-foreground'>{row.formula}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
